import { useEffect, useMemo, useState } from "react";
import * as api from "../../api/client";
import type { AdminPortrait } from "../../api/client";
import type { Theme } from "../../theme/pixel";
import { defaultBtn, withBtnStyle } from "../../theme/styles";
import { Banner, TextInput } from "./forms";

// Full-screen picker over `/portraits/npcs/*`. Each tile renders the
// portrait thumbnail with its filename as a caption. Filename-only
// labels are intentional — we don't pretty-print to speaker display
// names because the catalog doesn't yet cover every face under
// `npcs/` (cardmaster, quartermaster, antar, …). Clicking a tile
// commits the pick and closes the picker.
export function PortraitPicker({
  open,
  theme,
  onClose,
  onPick,
}: {
  open: boolean;
  theme: Theme;
  onClose: () => void;
  onPick: (filename: string) => void;
}) {
  const [portraits, setPortraits] = useState<AdminPortrait[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setError(null);
    void (async () => {
      try {
        const { portraits } = await api.listAdminPortraits();
        setPortraits(portraits);
      } catch (e) {
        setError(e instanceof Error ? e.message : "failed to load portraits");
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (!portraits) return [] as AdminPortrait[];
    const q = query.trim().toLowerCase();
    if (!q) return portraits;
    return portraits.filter((p) =>
      [p.filename, p.base, p.mood ?? ""].some((s) =>
        s.toLowerCase().includes(q),
      ),
    );
  }, [portraits, query]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10, 8, 4, 0.82)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdge}`,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          width: "100%",
          maxWidth: 960,
          maxHeight: "100%",
          minHeight: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 12,
              letterSpacing: theme.letterSpacing,
              color: theme.ink,
            }}
          >
            USE NPC IMAGE
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              theme={theme}
              value={query}
              onChange={setQuery}
              placeholder="Filter by filename, base, or mood…"
            />
          </div>
          <button
            onClick={onClose}
            {...withBtnStyle(defaultBtn(theme, false), {
              fontSize: 11,
              padding: "8px 16px",
            })}
          >
            CLOSE
          </button>
        </div>

        {error && <Banner kind="error" theme={theme}>{error}</Banner>}

        <div
          style={{
            overflowY: "auto",
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 12,
            padding: 4,
          }}
        >
          {portraits === null && !error && (
            <div style={{ color: theme.inkFaint, fontSize: 12 }}>Loading…</div>
          )}
          {portraits !== null && filtered.length === 0 && !error && (
            <div style={{ color: theme.inkFaint, fontSize: 12 }}>
              No portraits match.
            </div>
          )}
          {filtered.map((p) => (
            <PortraitTile
              key={p.filename}
              portrait={p}
              theme={theme}
              onPick={() => {
                onPick(p.filename);
                onClose();
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PortraitTile({
  portrait,
  theme,
  onPick,
}: {
  portrait: AdminPortrait;
  theme: Theme;
  onPick: () => void;
}) {
  return (
    <button
      onClick={onPick}
      title={portrait.filename}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 6,
        background: theme.panelSolid,
        border: `2px solid ${theme.panelEdge}`,
        color: theme.ink,
        cursor: "pointer",
        font: "inherit",
        textAlign: "left",
      }}
    >
      <div
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          background: theme.panel,
          overflow: "hidden",
        }}
      >
        <img
          src={`/portraits/npcs/${portrait.filename}`}
          alt=""
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            imageRendering: "pixelated",
            display: "block",
          }}
        />
      </div>
      <div
        style={{
          fontSize: 11,
          lineHeight: 1.3,
          wordBreak: "break-word",
        }}
      >
        {portrait.filename}
      </div>
    </button>
  );
}
